import router from '@/router'
import trainerGamestateActions from '@/store/gamestate/gamestate_trainer'
import { omit } from 'lodash'
import { getCardsFromStr, getVisibleCards } from '@/helpers/cards'
import { getRouteNameByStudyTab } from '@/store/router/router'
import {
  getEventsFromHistoryApi,
  getEventsNextActionsApi,
  getCompleteHistorySpots,
  getHistoryUrlParams,
  getActionStyles,
  setActiveHistorySpot,
  getSpotSelectedAction,
} from '@/store/gamestate/gamestate'
import { namespaces } from '@/helpers/constants'
import { SNK } from '@/helpers/helpers'
import * as R from 'remeda'

const getInitialState = ({ namespace }) => ({
  gamestate_namespace: namespace,
  history_spots: [],

  // trainer only
  gamestate_betinpot: false,
  gamestate_next_pot: false,
  gamestate_next_board: false,
  gamestate_next_player: false,
})

const actions = {
  gamestate_set_api_history_shortcut({ state, getters, rootGetters, commit, dispatch }, result) {
    if (rootGetters.poker_diss_history_calls(state.gamestate_namespace)) return

    if (result?.response?.status === 200) {
      const { data } = result.response
      const { namespace } = result
      const { history_spots } = getEventsFromHistoryApi({
        future_spots: data.future_actions,
        history_spots: data.history_actions,
      })

      const hSpot = history_spots.length - (data.future_actions?.length || 0) - 1
      const { path_name } = getters
      if (path_name === 'StudySolutions') {
        dispatch('gamestate_set_history', {
          history_spots,
          spot_id: hSpot,
          query: {
            board: '',
          },
          push: false,
        })
      } else {
        const actionName = `${namespace ? `${namespace}/` : ''}gamestate_set_history`
        dispatch(
          actionName,
          {
            history_spots,
            spot_id: hSpot,
            push: false,
          },
          { root: true },
        )
      }

      dispatch('poker_show_board_dialog', { namespace }, { root: true })
    }
  },
  gamestate_set_api_history({ state, getters, rootGetters, commit, dispatch }, result) {
    if (rootGetters.poker_diss_history_calls(state.gamestate_namespace)) return

    if (result?.response?.status === 200) {
      const { data } = result.response
      const namespace = result.namespace
      let query = result.query

      const routerParams = result.routerParams || {
        push: false,
        replace: true,
        saveLocally: true,
      }

      const { history_spots } = getEventsFromHistoryApi({
        future_spots: data.future_actions,
        history_spots: data.history_actions,
      })

      // buggy solutions can return empty history_spots after showdown
      const showdownIdx = history_spots.findIndex(s => s.type === 'showdown')
      if (showdownIdx >= 0 && showdownIdx < history_spots.length - 1) {
        history_spots.splice(showdownIdx + 1, history_spots.length - showdownIdx - 1)
      }

      const query_history_spot =
        rootGetters[`${namespace ? `${namespace}/` : ''}query_history_spot`]

      const l = history_spots.length - 1
      let hSpot = query_history_spot
        ? Number(query_history_spot)
        : l - (data.future_actions?.length || 0)

      // force fold skip first node
      const minStartSpotIdx = history_spots[0]?.forced_fold ? 1 : 0
      hSpot = isNaN(hSpot) ? minStartSpotIdx : hSpot
      hSpot = hSpot < minStartSpotIdx ? minStartSpotIdx : l < hSpot ? l : hSpot

      const history_spot = history_spots[hSpot]
      if (history_spot?.type === 'showdown') {
        hSpot -= 1
      }

      const spotsWithActions = history_spots.filter(spot => spot.selected_action_code)
      if (spotsWithActions.length < hSpot) {
        hSpot = spotsWithActions.length
      }

      const lastSpot = history_spots[l]
      if (lastSpot.custom_solution_id) {
        query = {
          ...query,
          board: lastSpot.board,
        }
      }

      dispatch('gamestate_set_history', {
        history_spots,
        query,
        spot_id: hSpot,
        ...routerParams,
      })

      dispatch('poker_show_board_dialog', { namespace }, { root: true })
      dispatch('router_set_metatitle', undefined, { root: true })
    } else {
      const errResponse = result?.error?.response
      const code = errResponse?.data?.code
      const status = errResponse?.status
      if (status === 400 && code === 'TREE_IS_TOO_BIG') {
        // message will be displayed in sol.browser
      } else {
        // 400 - bad request - bad url
        // -- maybe we should show a message here
        // debugger
        const { namespace } = result
        dispatch('gamestate_reset_history', { namespace })
      }
    }
  },
  gamestate_get_next_actions(
    { state, getters, commit, dispatch },
    { namespace, routerParams, cussol_id, enable_hsWatcher },
  ) {
    // if you go from trainer - reset next so sol browser dont use next
    // probably better to do this by route check in getter
    dispatch('gamestate_reset_next', {
      pot: true,
      betInPot: false,
      board: true,
      player: true,
    })

    dispatch(
      'api_poker_next_actions',
      { namespace, routerParams, cussol_id, enable_hsWatcher },
      { root: true },
    )
  },
  gamestate_set_next_actions({ state, getters, rootGetters, commit, dispatch }, result) {
    if (rootGetters.poker_diss_history_calls(state.gamestate_namespace)) return

    dispatch('gamestate_reset_next')

    if (result?.response?.status === 200) {
      const { data } = result.response
      const namespace = result.namespace
      const routerParams = result.routerParams || {
        push: false,
        replace: true,
        saveLocally: true,
      }

      const { history_spots } = getEventsNextActionsApi({
        future_spots: data.future_actions,
        next_actions: data.next_actions,
        // future_spots,
        // next_actions,
        history_spots: state.history_spots,
      })
      let hSpot = history_spots.length - (data.future_actions?.length || 0) - 1
      const history_spot = history_spots[hSpot]
      if (history_spot?.type === 'showdown') {
        hSpot -= 1
      }
      dispatch('gamestate_set_history', {
        history_spots,
        spot_id: hSpot,
        // push: false,
        ...routerParams,
      })

      dispatch('poker_show_board_dialog', { namespace }, { root: true })
    }
  },
  // gamestate_set_solution_end_node({ state, getters, commit, dispatch }, { currentSpot, action }) {
  //   const { history_spots } = getFlopShowdownNode({
  //     action,
  //     currentSpot,
  //     history_spots: state.history_spots,
  //   })
  //   let hSpot = history_spots.length - 1
  //   dispatch('gamestate_set_history', {
  //     history_spots,
  //     spot_id: hSpot,
  //   })
  // },
  gamestate_set_api_practice_builder({ state, getters, commit, dispatch }, result) {
    if (result?.response?.status === 200) {
      const { board } = result.response?.data
      dispatch('router_update_query', {
        query: {
          board_tmp: board,
        },
        push: false,
      })
    }
  },
  gamestate_set_history(
    { state, getters, commit, dispatch },
    { history_spots, spot_id, query = {}, push = true, replace = true, saveLocally = true },
  ) {
    let hSpots = history_spots
    hSpots = setActiveHistorySpot({ history_spots, spot_id })
    commit('gamestate_set', {
      key: 'history_spots',
      value: hSpots,
    })

    const historyParams = getHistoryUrlParams({ history_spots: hSpots })

    const lastSpot = hSpots[hSpots.length - 1]
    const cussol_id = lastSpot?.custom_solution_id

    const newQuery = {
      // ...query,
      // ...historyParams,
      history_spot: spot_id,
      ...(cussol_id ? { cussol_id } : {}),
      ...historyParams,
      ...query,
    }
    // console.log(newQuery)
    dispatch(`router_update_query`, {
      query: newQuery,
      push,
      replace,
      saveLocally,
    })
  },
  gamestate_reset_history(
    { state, getters, commit, dispatch },
    {
      query = {},
      routerParams = {
        push: false,
        replace: true,
        saveLocally: true,
      },
    } = {},
  ) {
    commit('gamestate_set', {
      key: 'history_spots',
      value: [],
    })
    dispatch(`router_update_query`, {
      query: {
        history_spot: '',
        preflop_actions: '',
        flop_actions: '',
        turn_actions: '',
        river_actions: '',
        ...query,
      },
      ...routerParams,
    })
  },
  gamestate_repaint_actions({ state, commit, dispatch, getters, rootGetters }, trigger) {
    const { history_spots, history_spot } = getters
    // debugger
    // console.log(trigger)
    if (history_spot) {
      const res = history_spots.map(e => {
        let aa = e.available_actions
        aa = getActionStyles({ available_actions: aa })
        const r = {
          ...e,
          available_actions: aa,
        }
        return r
      })
      commit('gamestate_set', {
        key: 'history_spots',
        value: res,
      })
    }

    const pathName = getRouteNameByStudyTab(router, rootGetters)
    if (
      !state.gamestate_namespace &&
      (pathName === 'StudySolutions' ||
        pathName === 'StudyReports' ||
        pathName === 'PracticeRangeBuilder')
    ) {
      if (pathName === 'PracticeRangeBuilder') {
        dispatch('globals_action', 'rangeBuilder_repaint', { root: true })
        return
      }
      if (pathName === 'StudyReports') {
        const repResult = rootGetters.api_get('api_poker_reports')
        const result = {
          response: {
            data: repResult?.response?.data,
            status: repResult?.status,
          },
        }
        if (repResult) {
          dispatch('rep_data_initialize', result, { root: true })
        }
        return
      }
      const solData = rootGetters.api_get_data('api_poker_solution')
      if (solData) {
        dispatch('sol_data_setSolProps', { apiData: solData }, { root: true })
        dispatch(
          'sol_detail_initialize',
          { tableGroups: getters.sol_data_tableGroups() },
          { root: true },
        )
      }
    }
  },
  gamestate_set({ state, commit, dispatch, getters, rootGetters }, { key, value }) {
    commit('gamestate_set', { key, value })
  },
  gamestate_applySolution(
    { state, commit, dispatch, getters },
    { solData, callHistory = false, callNextActions = false, updateUrlImmediately = false },
  ) {
    const updateUrl = () => {
      const query = {
        cussol_id: solData.id,
        custree_id: solData.custom_tree.id,
        board: solData.board,
        solution_type: 'custom',
      }

      dispatch('router_update_query', {
        query,
        push: false,
        replace: true,
        saveLocally: false,
      })
    }
    if (updateUrlImmediately) {
      updateUrl()
    }

    dispatch('globals_action', 'disable_history_watcher', { root: true })
    setTimeout(() => {
      if (!updateUrlImmediately) {
        updateUrl()
      }

      if (callHistory) {
        dispatch(
          'api_poker_history',
          {
            cussol_id: solData.id,
            namespace: state.gamestate_namespace,
            enable_hsWatcher: true,
          },
          { root: true },
        )
        return
      }
      if (callNextActions) {
        dispatch('gamestate_get_next_actions', {
          cussol_id: solData.id,
          namespace: state.gamestate_namespace,
          enable_hsWatcher: true,
        })
      }
    }, 0)
  },
}

const getters = {
  preflopOnlyGametype: (state, getters) => {
    const { history_spot, gameformat } = getters

    return (
      gameformat?.preflop_only &&
      history_spot &&
      (history_spot?.type === 'showdown' || history_spot?.street !== 'preflop')
    )
  },
  solparams_strategy: (state, getters, rootState, rootGetters) => {
    let { history_spots, history_spot, street, gameformat } = getters
    const solElite = getters.sol_elite
    const custom_solution_id = solElite.want_use
      ? history_spot.custom_solution_id || solElite.custom_solution_id
      : null

    let res = {}
    history_spots = history_spots.filter(e => e.id < history_spot?.id)
    const hp = getHistoryUrlParams({ history_spots, includeShowdown: false })
    const board = getters
      .street_board_any(street)
      .filter(c => !c.includes('E'))
      .join('')

    const common = {
      preflop_actions: hp.preflop_actions,
      flop_actions: hp.flop_actions,
      turn_actions: hp.turn_actions,
      river_actions: hp.river_actions,
      board,
    }
    if (custom_solution_id) {
      // custom ai solutions
      res = {
        custom_solution_id,
        ...common,
      }
    } else {
      // gwiz solutions
      res = {
        gametype: gameformat.gametype,
        depth: gameformat.depth,
        stacks: gameformat.stacks,
        ...common,
      }
    }
    return res
  },
  rep_group_params: (state, getters, rootState, rootGetters) => {
    // builds the required group & filters param required for the API query
    const groupParam = {
      swv_high_cards: 'HIGH_CARD',
      swv_suits: 'SUITS',
      swv_pairing: 'PAIRING',
      swv_connectedness: 'CONNECTEDNESS',
      swv_card_ranks: 'CARD_RANKS',
    }

    let param
    let { street } = getters
    const reportsTab =
      street === 'flop'
        ? rootGetters.sw_reports_flop_grouping
        : rootGetters.sw_reports_turn_grouping

    param = {
      ...(groupParam ? { group: groupParam[reportsTab] } : {}),
      ...(rootGetters.ffrf.params || {}),
    }
    return param
  },
  solparams_reports: (state, getters, rootState, rootGetters) => {
    let {
      history_spots, //
      history_spot,
      gameformat,
      street,
    } = getters
    let {
      ffrf, // filters_reports_flop
    } = rootGetters
    history_spots = history_spots.filter(e => e.id < history_spot?.id)
    const hp = getHistoryUrlParams({ history_spots, includeShowdown: false })
    const sol_elite = getters.sol_elite

    const board = getters
      // on Flop no board matters. On Turn only flop-board matters
      // agg reports are all flops anyway, turn reports are all turns + flop-board
      .street_board_any(street === 'turn' ? 'flop' : 'preflop')
      .filter(c => !c.includes('E'))
      .join('')
    const params = {
      limit: 99999,
      offset: 0,
      ...(getters.sol_usingCussolReports
        ? {
            custom_tree_id: sol_elite.custom_tree_id,
            ...(sol_elite.starting_street === 'PREFLOP'
              ? { preflop_actions: hp.preflop_actions }
              : {}),
            flop_actions: hp.flop_actions,
          }
        : {
            gametype: gameformat.gametype,
            depth: gameformat.depth,
            stacks: gameformat.stacks,
            preflop_actions: hp.preflop_actions,
            flop_actions: hp.flop_actions,
            turn_actions: hp.turn_actions,
            river_actions: hp.river_actions,
            ...(street === 'turn' ? { board } : {}),
            ...(street === 'flop' ? ffrf.params || {} : {}),
            ...(rootGetters.rep_turn_filterHand ? { hand: rootGetters.rep_turn_filterHand } : {}),
          }),
      ...getters.rep_group_params,
    }

    const endpoint = 'aggregated-report/'

    const res = { endpoint, params }

    return res
  },

  gamestate_state_props: state => omit(state, 'gamestate_namespace'),
  gamestate_betinpot: state => state.gamestate_betinpot,

  gamestate_next_pot: state => state.gamestate_next_pot,
  gamestate_next_board: state => state.gamestate_next_board,
  gamestate_next_player: state => state.gamestate_next_player,

  history_spots: state => state.history_spots,

  history_spot_trainer: (state, getters) => {
    const res = getters.history_spots.find(i => i.is_active) || getters.history_spots[0]
    return res || null
  },
  available_actions: (state, getters) => getters.history_spot_trainer?.available_actions || [],
  history_spots_complete: (state, getters, rootState, rootGetters) => {
    const { query_board } = getters

    // has inserted street-board spots
    // is stripped of solutions data
    // has corrected street for showdown
    // (showdown-spot is returned with next-street if last action was next_street:true)
    let res = getCompleteHistorySpots({
      history_spots: state.history_spots,
      board: query_board,
      // addMissingBoardSpots = false,
      // isPreflop = false,
    })

    // Handles - "Add board" spot when creating custom solution
    // cos cards-dialog nees to know how many cards to show
    // #REFACTOR:NAMESPACES
    const pathName = getRouteNameByStudyTab(router, rootGetters)
    const solRepNamespace = {
      StudySolutions: namespaces.STUDY,
      StudyReports: namespaces.REPORTS,
    }[pathName]

    const solution_type =
      rootGetters[SNK('query_solution_type', solRepNamespace || state.gamestate_namespace)]
    const solElite = rootGetters[SNK('sol_elite', solRepNamespace || state.gamestate_namespace)]
    if (
      solution_type === 'custom' && //
      solElite?.starting_street === 'FLOP' &&
      !res.length
    ) {
      res = [
        {
          id: 'flop',
          street: 'flop',
          type: 'board',
          placeholder: true,
        },
      ]
    }

    // console.log(res)
    return res
  },
  history_spot: (state, getters) => {
    const res =
      getters.history_spots_complete.find(i => i.is_active) || getters.history_spots_complete[0]
    return res || null
  },
  can_ai_solve_previous_action: (state, getters) => {
    const history_spot_index = getters.history_spots_complete.findIndex(i => i.is_active)
    const prevSpot = getters.history_spots_complete[history_spot_index - 1]

    const selectedAction = getSpotSelectedAction({ spot: prevSpot })
    return selectedAction?.can_ai_solve
  },
  history_spot_id: (state, getters) => {
    const { history_spot } = getters
    const res = (history_spot?.id ?? '').toString() || null
    // const res = (history_spot?.uid ?? '').toString() || null
    return res
  },
  hero_spot: (state, getters) => {
    const { history_spot } = getters
    const res = !history_spot?.is_bot ? (history_spot?.id ?? '').toString() || null : null
    return res
  },
  bot_spot: (state, getters) => {
    const { history_spot } = getters
    const res = history_spot?.is_bot ? (history_spot?.id ?? '').toString() || null : null
    return res
  },
  showdown_spot: (state, getters) => {
    const { history_spot } = getters
    const res = history_spot && history_spot?.type === 'showdown'
    return res
  },
  last_spot: (state, getters) => {
    const { history_spots } = getters
    const res = history_spots[history_spots.length - 1]
    return res
  },
  street: (state, getters) => {
    const { history_spot } = getters
    return history_spot?.street || 'preflop'
  },
  pot: (state, getters) => {
    const { history_spot } = getters
    return history_spot?.pot || 0
  },
  start_pot: (state, getters) => {
    const { history_spot } = getters
    return history_spot?.start_pot || 0
  },
  // grouped_actions: (state, getters) => getters.history_spot?.available_actions || [],
  // simple_actions: (state, getters) => getters.history_spot?.available_actions || [],
  preselected_action: (state, getters) => {
    const res = getters.available_actions.find(a => a.is_preselected)
    return res
  },
  history_spot_board: (state, getters) => {
    const { street } = getters
    const cards = getters.street_board(street)
    return cards
  },
  street_board:
    (state, getters) =>
    (street = 'preflop') => {
      const { query_board } = getters
      const visibleCards = getVisibleCards(street)
      const cards = [...getCardsFromStr(query_board), 'Ee', 'Ee', 'Ee', 'Ee', 'Ee'].slice(
        0,
        visibleCards,
      )
      return cards
    },
  street_board_tmp:
    (state, getters) =>
    (street = 'preflop') => {
      const { query_board_tmp } = getters
      const visibleCards = getVisibleCards(street)
      const cards = [...getCardsFromStr(query_board_tmp), 'Ee', 'Ee', 'Ee', 'Ee', 'Ee'].slice(
        0,
        visibleCards,
      )
      return cards
    },
  street_board_any:
    (state, getters) =>
    (street = 'preflop') => {
      const { query_board_tmp, query_board } = getters
      const visibleCards = getVisibleCards(street)
      const cards = [
        ...getCardsFromStr(query_board_tmp || query_board),
        'Ee',
        'Ee',
        'Ee',
        'Ee',
        'Ee',
      ].slice(0, visibleCards)
      return cards
    },
  flop_board: (state, getters) =>
    getters
      .street_board_any('flop')
      .filter(c => !c.includes('E'))
      .join(''),

  last_spot_street: (state, getters, rootGetters) => {
    const { history_spots_complete } = getters
    return history_spots_complete[history_spots_complete.length - 1]?.street || 'preflop'
  },
  gamestate_visible_cards: (state, getters) => {
    const { history_spots_complete } = getters
    const street = history_spots_complete[history_spots_complete.length - 1]?.street || 'preflop'
    const res = street === 'preflop' ? 0 : street === 'flop' ? 3 : street === 'turn' ? 4 : 5
    return res
  },
  gamestate: state => key => state.gameformat,
  gamestate_get: state => key => state[key],

  show_bets_in: (state, getters) => getters.mergedUserSettings.showBetsIn,
  show_ev_in: (state, getters) => getters.mergedUserSettings.showEvIn,
  show_ev_icm_in: (state, getters) => getters.mergedUserSettings.showEvIcmIn,
  // TODO remove this
  show_amount_by_pot: (state, getters) => getters.mergedUserSettings.showBetsIn === 'pot',

  // TODO: remove this after ICM 3-way is supported
  gamestate_is_icm_and_has_3_players: (state, getters) => {
    // Must be ICM
    if (getters.gameformat.format !== 'icm') return false

    // Must be the last preflop spot check
    const lastPreflopSpot = R.findLast(
      getters.history_spots_complete,
      i => i.street === 'preflop' && i.player,
    )
    const selectedAction = getSpotSelectedAction({ spot: lastPreflopSpot })
    if (!selectedAction) return false
    if (selectedAction.code !== 'C') return false

    // Must be 3 players in the solution
    const historySpot = getters.history_spot
    const playersNotFolded = historySpot?.players?.reduce(
      (acc, player) => acc + (player.is_folded !== true ? 1 : 0),
      0,
    )
    return playersNotFolded === 3
  },
}

const mutations = {
  gamestate_reset(state, initialState) {
    Object.assign(state, initialState)
  },
  gamestate_set(state, { key, value }) {
    state[key] = value
  },
}

function getGamestateModule(namespace) {
  return {
    state: getInitialState({ namespace }),
    getters,
    actions: {
      ...actions,
      ...trainerGamestateActions,
    },
    mutations,
  }
}
export { getGamestateModule }
